import type { ChildProcess } from "node:child_process";
import {
  buildSessionPreviewPorts,
  loadForwardPorts,
  type ConveyorConfig,
  type ForwardPortsResult,
} from "./config.js";
import { awaitGitReady, type GitReadyState } from "./git-ready.js";
import { runSetupCommand, runStartCommand, terminateProcessGroup } from "./commands.js";
import { startLazySidecars, waitForSidecars } from "./sidecars.js";

type OutputStream = "stdout" | "stderr";
type StartCommandChild = ReturnType<typeof runStartCommand>;

export interface WorkspaceCommandConnection {
  sendEvent(event: { type: string; [key: string]: unknown }): void;
  onRunStartCommand(callback: () => void): void;
}

export interface WorkspaceCommandSupervisorOptions {
  config: ConveyorConfig | null;
  workspaceDir: string;
  connection: WorkspaceCommandConnection;
  env?: NodeJS.ProcessEnv;
  awaitGitReady?: (options: {
    onLog: (message: string) => void;
    signal: AbortSignal;
  }) => Promise<GitReadyState>;
  startLazySidecars?: (
    env: NodeJS.ProcessEnv,
    onLog: (message: string) => void,
    signal: AbortSignal,
  ) => Promise<void>;
  waitForSidecars?: (options: {
    onLog: (message: string) => void;
    startLazy: false;
    signal: AbortSignal;
  }) => Promise<void>;
  runSetupCommand?: (
    command: string,
    cwd: string,
    onOutput: (stream: OutputStream, data: string) => void,
    signal: AbortSignal,
  ) => Promise<void>;
  runStartCommand?: typeof runStartCommand;
  loadForwardPorts?: (workspaceDir: string) => Promise<ForwardPortsResult>;
  writeOutput?: (stream: OutputStream, data: string) => void;
  terminateStartCommand?: (child: StartCommandChild) => void | Promise<void>;
}

const defaultWriteOutput = (stream: OutputStream, data: string): void => {
  (stream === "stderr" ? process.stderr : process.stdout).write(data);
};

export function stopWorkspaceCommands(
  supervisor: { stop(): Promise<void> } | null | undefined,
): Promise<void> {
  return supervisor?.stop() ?? Promise.resolve();
}

export interface WorkspaceCommandStartupOptions<T extends { start(): void }> {
  connect: () => Promise<boolean>;
  isShuttingDown: () => boolean;
  create: () => T;
}

/**
 * Holds workspace commands behind both the runner's connection/baseline gate
 * and the CLI's synchronous shutdown intent. A signal can arrive while
 * connect() is awaiting discovery but before runner.stop() runs after the
 * graceful git flush; checking shutdown intent after the await closes that
 * window without constructing a supervisor.
 */
export async function startWorkspaceCommandsAfterConnect<T extends { start(): void }>(
  options: WorkspaceCommandStartupOptions<T>,
): Promise<T | null> {
  const connected = await options.connect();
  if (!connected || options.isShuttingDown()) return null;
  const supervisor = options.create();
  supervisor.start();
  return supervisor;
}

/**
 * Owns setup and long-running workspace commands independently of the runner
 * that hosts them. start() is intentionally fire-and-forget: command failures
 * are user-visible events and never terminate the agent lifecycle.
 */
export class WorkspaceCommandSupervisor {
  private readonly abortController = new AbortController();
  private readonly expectedStartCommandStops = new WeakSet<ChildProcess>();
  private readonly config: ConveyorConfig | null;
  private readonly workspaceDir: string;
  private readonly connection: WorkspaceCommandConnection;
  private readonly env: NodeJS.ProcessEnv;
  private readonly awaitGitReadyFn: NonNullable<WorkspaceCommandSupervisorOptions["awaitGitReady"]>;
  private readonly startLazySidecarsFn: NonNullable<
    WorkspaceCommandSupervisorOptions["startLazySidecars"]
  >;
  private readonly waitForSidecarsFn: NonNullable<
    WorkspaceCommandSupervisorOptions["waitForSidecars"]
  >;
  private readonly runSetupCommandFn: NonNullable<
    WorkspaceCommandSupervisorOptions["runSetupCommand"]
  >;
  private readonly runStartCommandFn: typeof runStartCommand;
  private readonly loadForwardPortsFn: (workspaceDir: string) => Promise<ForwardPortsResult>;
  private readonly writeOutput: (stream: OutputStream, data: string) => void;
  private readonly terminateStartCommand: (child: StartCommandChild) => void | Promise<void>;

  private startCommandChild: StartCommandChild | null = null;
  private readonly liveStartCommandChildren = new Set<StartCommandChild>();
  private readonly startCommandTerminations = new WeakMap<StartCommandChild, Promise<void>>();
  private readonly startCommandEndPromises = new WeakMap<StartCommandChild, Promise<void>>();
  private readonly resolveStartCommandEnd = new WeakMap<StartCommandChild, () => void>();
  private readonly backgroundTasks = new Set<Promise<void>>();
  private startCommandQueue: Promise<void> = Promise.resolve();
  private shutdownPromise: Promise<void> | null = null;
  private startCommandLaunchRequested = false;
  private started = false;
  private stopped = false;

  constructor(options: WorkspaceCommandSupervisorOptions) {
    this.config = options.config;
    this.workspaceDir = options.workspaceDir;
    this.connection = options.connection;
    this.env = options.env ?? process.env;
    this.awaitGitReadyFn =
      options.awaitGitReady ??
      ((opts) =>
        awaitGitReady({
          onLog: opts.onLog,
          signal: opts.signal,
        }));
    this.startLazySidecarsFn =
      options.startLazySidecars ?? ((env, onLog, signal) => startLazySidecars(env, onLog, signal));
    this.waitForSidecarsFn =
      options.waitForSidecars ??
      ((opts) =>
        waitForSidecars({ onLog: opts.onLog, startLazy: opts.startLazy, signal: opts.signal }));
    this.runSetupCommandFn =
      options.runSetupCommand ??
      ((command, cwd, onOutput, signal) => runSetupCommand(command, cwd, onOutput, signal));
    this.runStartCommandFn = options.runStartCommand ?? runStartCommand;
    this.loadForwardPortsFn = options.loadForwardPorts ?? loadForwardPorts;
    this.writeOutput = options.writeOutput ?? defaultWriteOutput;
    this.terminateStartCommand = options.terminateStartCommand ?? terminateProcessGroup;
  }

  start(): void {
    if (this.started || this.stopped) return;
    this.started = true;
    this.connection.onRunStartCommand(() => this.restartStartCommand());

    this.trackBackgroundTask(
      this.startLazySidecarsFn(
        this.env,
        (message) => this.forwardSetupOutput("stdout", `[sidecars] ${message}\n`),
        this.abortController.signal,
      ).catch((error: unknown) => this.reportUnexpectedError(error)),
    );

    if (this.config) {
      this.trackBackgroundTask(
        this.runSetupAndStart().catch((error: unknown) => this.reportUnexpectedError(error)),
      );
    }
  }

  stop(): Promise<void> {
    if (this.shutdownPromise) return this.shutdownPromise;
    this.stopped = true;
    this.abortController.abort();
    const backgroundTasks = [...this.backgroundTasks];
    const termination = this.terminateAllStartCommands();
    this.shutdownPromise = (async () => {
      await Promise.allSettled([termination, this.startCommandQueue, ...backgroundTasks]);
      await this.terminateAllStartCommands();
    })();
    return this.shutdownPromise;
  }

  private async runSetupAndStart(): Promise<void> {
    const gitState = await this.awaitGitReadyFn({
      onLog: (message) => this.forwardSetupOutput("stdout", `[git] ${message}\n`),
      signal: this.abortController.signal,
    });
    if (this.stopped) return;
    if (gitState === "failed" || gitState === "timeout") {
      this.connection.sendEvent({
        type: "setup_error",
        message: "Workspace not ready — skipping setup/start",
      });
      return;
    }

    await this.waitForSidecarsFn({
      onLog: (message) => this.forwardSetupOutput("stdout", `[sidecars] ${message}\n`),
      startLazy: false,
      signal: this.abortController.signal,
    });
    if (this.stopped) return;

    await this.runConfiguredSetup();
    if (this.stopped) return;

    const startCommandRunning = this.config?.startCommand
      ? await this.ensureStartCommandLaunched(this.config.startCommand)
      : false;
    if (this.stopped) return;

    const forwardPorts = await this.loadForwardPortsFn(this.workspaceDir);
    if (this.stopped) return;
    const previewPorts = buildSessionPreviewPorts(forwardPorts);
    this.connection.sendEvent({
      type: "setup_complete",
      startCommandRunning,
      ...(previewPorts.length > 0 ? { previewPorts } : {}),
    });
  }

  private async runConfiguredSetup(): Promise<void> {
    const command = this.config?.setupCommand;
    if (!command) return;
    try {
      await this.runSetupCommandFn(
        command,
        this.workspaceDir,
        (stream, data) => this.forwardSetupOutput(stream, data),
        this.abortController.signal,
      );
    } catch (error) {
      if (this.stopped) return;
      this.connection.sendEvent({
        type: "setup_error",
        message: error instanceof Error ? error.message : "Setup command failed",
      });
    }
  }

  private restartStartCommand(): void {
    if (this.stopped || !this.config?.startCommand) return;
    this.connection.sendEvent({
      type: "start_command_output",
      stream: "stdout",
      data: "[conveyor-agent] Restarting start command...\n",
    });
    void this.enqueueStartCommandReplacement(this.config.startCommand);
  }

  private async ensureStartCommandLaunched(command: string): Promise<boolean> {
    if (!this.startCommandLaunchRequested) {
      return this.enqueueStartCommandReplacement(command);
    }
    await this.startCommandQueue;
    return !this.stopped && this.liveStartCommandChildren.size > 0;
  }

  private enqueueStartCommandReplacement(command: string): Promise<boolean> {
    this.startCommandLaunchRequested = true;
    const operation = this.startCommandQueue.then(async () => {
      if (this.stopped) return false;
      await this.terminateAllStartCommands();
      if (this.stopped) return false;
      return this.launchStartCommand(command);
    });
    this.startCommandQueue = operation.then(
      () => undefined,
      () => undefined,
    );
    return operation;
  }

  private launchStartCommand(command: string): boolean {
    if (this.stopped) return false;
    this.connection.sendEvent({ type: "start_command_started" });
    try {
      const child = this.runStartCommandFn(command, this.workspaceDir, (stream, data) => {
        if (this.stopped) return;
        this.connection.sendEvent({ type: "start_command_output", stream, data });
        this.writeOutput(stream, data);
      });
      this.liveStartCommandChildren.add(child);
      const ended = new Promise<void>((resolve) => {
        this.resolveStartCommandEnd.set(child, resolve);
      });
      this.startCommandEndPromises.set(child, ended);
      if (this.stopped) {
        void this.terminateStartCommandChild(child);
        return false;
      }
      this.startCommandChild = child;
      child.on("exit", (code, signal) => {
        if (this.startCommandChild === child) this.startCommandChild = null;
        this.liveStartCommandChildren.delete(child);
        this.settleStartCommandEnd(child);
        if (this.stopped || this.expectedStartCommandStops.has(child)) return;
        const message = `start command exited${code === null ? "" : ` with code ${code}`}${signal ? ` (signal: ${signal})` : ""}`;
        this.connection.sendEvent({ type: "start_command_exited", code, signal, message });
        if (code !== null && code !== 0) {
          this.connection.sendEvent({
            type: "start_command_error",
            message: `start command exited with code ${code}${signal ? ` (signal: ${signal})` : ""}`,
          });
        }
      });
      child.on("error", (error) => {
        if (child.pid === undefined) {
          if (this.startCommandChild === child) this.startCommandChild = null;
          this.liveStartCommandChildren.delete(child);
          this.settleStartCommandEnd(child);
          this.startCommandTerminations.delete(child);
        }
        if (this.stopped) return;
        this.connection.sendEvent({ type: "start_command_error", message: error.message });
      });
      return true;
    } catch (error) {
      if (!this.stopped) {
        this.connection.sendEvent({
          type: "start_command_error",
          message: error instanceof Error ? error.message : String(error),
        });
      }
      return false;
    }
  }

  private terminateStartCommandChild(child: StartCommandChild): Promise<void> {
    const existing = this.startCommandTerminations.get(child);
    if (existing) return existing;
    this.expectedStartCommandStops.add(child);
    const processTermination = Promise.resolve(this.terminateStartCommand(child));
    const ended = this.startCommandEndPromises.get(child);
    const termination = (ended ? Promise.race([processTermination, ended]) : processTermination)
      .then(() => undefined)
      .finally(() => this.startCommandTerminations.delete(child));
    this.startCommandTerminations.set(child, termination);
    return termination;
  }

  private async terminateAllStartCommands(): Promise<void> {
    const live = [...this.liveStartCommandChildren].filter((child) => child.exitCode === null);
    await Promise.all(live.map((child) => this.terminateStartCommandChild(child)));
  }

  private settleStartCommandEnd(child: StartCommandChild): void {
    this.resolveStartCommandEnd.get(child)?.();
    this.resolveStartCommandEnd.delete(child);
    this.startCommandEndPromises.delete(child);
  }

  private trackBackgroundTask(task: Promise<void>): void {
    this.backgroundTasks.add(task);
    void task.finally(() => this.backgroundTasks.delete(task));
  }

  private forwardSetupOutput(stream: OutputStream, data: string): void {
    if (this.stopped) return;
    this.connection.sendEvent({ type: "setup_output", stream, data });
    this.writeOutput(stream, data);
  }

  private reportUnexpectedError(error: unknown): void {
    if (this.stopped) return;
    this.connection.sendEvent({
      type: "setup_error",
      message: error instanceof Error ? error.message : String(error),
    });
  }
}
